import { apiClient } from '@shared/api';
import type { ApiResponse, PagedResult } from '@shared/types';
import { PricingDriftError, extractPricingDrift } from '../types/pricingDrift.types';
import type { BaseMarkup } from '@features/quotation-intake/types/quotationIntake.types';
import {
  type QuotationDraft,
  type QuotationLineItemDto,
  type QuotationListItemDto,
  type QuotationListParams,
  type QuotationListResult,
  type QuotationStats,
  type QuotationStatus,
  type QuotationSourceEmailSummary,
  type QuotationAttachment,
  type QuotationSubmissionSnapshot,
  DEFAULT_PRICE_LEVEL,
  EmailExtractionStatus,
} from '../types/quotation.types';
import {
  buildPricingSnapshot,
  computePricing,
  computePricingContext,
  markupForGrade,
  PRICING_RULE_VERSION,
  type PricingSnapshot,
} from '../utils/pricing';
import { dropQuotationKilogramRows, isPostedRow } from '../utils/lineItem';

// -----------------------------------------------------------------------------
// Wire types — shape of payloads crossing the network. Mirrors CSC_CORE DTOs.
// -----------------------------------------------------------------------------

interface SaveQuotationDraftLineItemDto {
  clientId: string;
  sortOrder: number;
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: string | null;
  isWeldable: boolean;
  quantity: number | null;
  unit: string;
  weightKg: number | null;
  matchStatus: string | null;
  rawDescription: string | null;
  notes: string | null;
  matchedSalesDescription: string | null;
  matchedItemId: string | null;
  priceLevel: string | null;
  basePricePerPc: number | null;
  averagePricePerPc: number | null;
  originalSpecJson: string | null;
  isEdited: boolean;
  promotedFromGrade: number | null;
}

interface SaveQuotationDraftRequestDto {
  id?: number | null;
  customerName: string;
  customerId: string | null;
  customerEntityId: string | null;
  headMainOffice: string;
  headMainOfficeCustomerId: string | null;
  projectCategoryId: string | null;
  projectCategoryName: string | null;
  projectName: string;
  termsId: string | null;
  termsName: string | null;
  salesRepId: string | null;
  salesCoordId: string | null;
  taxRate: number | null;
  date: string;
  escalationDate: string;
  expirationDate: string;
  pickup: string;
  waivedFreight: string;
  // 86d32r9rv — single freight input JSON; server parses and recomputes the charge.
  freightInputDraftJson: string | null;
  // 86d32t157 Phase 3 — pricing-drift baseline. Client sends what it showed sales;
  // server fills in bracketId per line and persists verbatim to PricingSnapshotJson.
  pricingSnapshot: PricingSnapshot | null;
  baseMarkupPct: number;
  gradeMarkupsJson: string | null;
  extractionSource: string;
  extractionText: string | null;
  extractionFileName: string | null;
  lineItems: SaveQuotationDraftLineItemDto[];
}

interface QuotationLineItemWireDto {
  clientId: string;
  sortOrder: number;
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: string | null;
  isWeldable: boolean;
  quantity: number | null;
  unit: string;
  weightKg: number | null;
  matchStatus: string | null;
  rawDescription: string | null;
  notes: string | null;
  matchedSalesDescription: string | null;
  matchedItemId: string | null;
  priceLevel: string | null;
  basePricePerPc: number | null;
  averagePricePerPc: number | null;
  originalSpecJson: string | null;
  isEdited: boolean;
  promotedFromGrade: number | null;
}

interface QuotationWireDto {
  id: number;
  quotationNumber: string;
  status: QuotationStatus;
  customerName: string;
  customerId: string | null;
  customerEntityId: string | null;
  headMainOffice: string;
  headMainOfficeCustomerId: string | null;
  projectCategoryId: string | null;
  projectCategoryName: string | null;
  projectName: string;
  termsId: string | null;
  termsName: string | null;
  salesRepId: string | null;
  salesCoordId: string | null;
  taxRate: number | null;
  date: string;
  escalationDate: string;
  expirationDate: string;
  pickup: string;
  waivedFreight: string;
  // 86d32r9rv — raw JSON for the discriminated-union freight model.
  freightInputDraftJson: string | null;
  freightChargeJson: string | null;
  freightSnapshotJson: string | null;
  baseMarkupPct: number;
  gradeMarkupsJson: string | null;
  extractionSource: string;
  extractionText: string | null;
  extractionFileName: string | null;
  cuttingListNumber: string | null;
  postingFailedReason?: string | null;
  netSuiteInternalId: string | null;
  netSuiteUrl: string | null;
  postedAt: string | null;
  postedById: number | null;
  postedByName: string | null;
  createdById: number;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string | null;
  isArchived: boolean;
  archivedAt: string | null;
  archivedById: number | null;
  lineItems: QuotationLineItemWireDto[];
  sourceEmail: QuotationSourceEmailSummary | null;
  attachments: QuotationAttachment[];
  // Wire field — backend's persisted column. Mapped to `emailExtractionStatus` on the
  // draft to disambiguate from the manual-flow string-union ExtractionStatus.
  extractionStatus: EmailExtractionStatus;
  // 14ykpqtr22u — the frozen record of what posted (QuotationDto.SubmissionSnapshot). Null on
  // drafts and on quotations posted before the snapshot column existed.
  submissionSnapshot?: QuotationSubmissionSnapshot | null;
}

// -----------------------------------------------------------------------------
// DTO ↔ draft mapping helpers
// -----------------------------------------------------------------------------

function toLineItemWire(li: QuotationLineItemDto, sortOrder: number): SaveQuotationDraftLineItemDto {
  return {
    clientId: li.id,
    sortOrder,
    diameterMm: li.diameterMm,
    lengthM: li.lengthM,
    gradeCode: li.gradeCode,
    isWeldable: li.isWeldable,
    quantity: li.quantity,
    unit: li.unit,
    weightKg: li.weightKg,
    matchStatus: li.matchStatus ?? null,
    rawDescription: li.rawDescription ?? null,
    notes: li.notes ?? null,
    matchedSalesDescription: li.matchedSalesDescription ?? null,
    matchedItemId: li.matchedItemId ?? null,
    // 86d39ammy: never send null — fall back to the displayed/effective default so the
    // persisted value always matches what the user sees ("Base Price").
    priceLevel: li.priceLevel ?? DEFAULT_PRICE_LEVEL,
    basePricePerPc: li.basePricePerPc ?? null,
    averagePricePerPc: li.averagePricePerPc ?? null,
    originalSpecJson: li.originalSpec ? JSON.stringify(li.originalSpec) : null,
    isEdited: !!li.isEdited,
    // CSC-291: round-trip the per-line promotion marker so save-then-reload preserves
    // the badge.
    promotedFromGrade: li.promotedFromGrade ?? null,
  };
}

function draftToSaveRequest(
  draft: QuotationDraft,
  baseMarkup: BaseMarkup | null,
): SaveQuotationDraftRequestDto {
  return {
    id: draft.serverId ?? null,
    customerName: draft.customerName ?? '',
    customerId: draft.customerId,
    customerEntityId: draft.customerEntityId,
    headMainOffice: draft.headMainOffice ?? '',
    headMainOfficeCustomerId: draft.headMainOfficeCustomerId,
    projectCategoryId: draft.projectCategoryId,
    projectCategoryName: draft.projectCategoryName,
    projectName: draft.projectName ?? '',
    termsId: draft.termsId,
    termsName: draft.termsName,
    salesRepId: draft.salesRepId,
    salesCoordId: draft.salesCoordId,
    taxRate: draft.taxRate,
    date: draft.date,
    escalationDate: draft.escalationDate,
    expirationDate: draft.expirationDate,
    pickup: String(draft.pickup),
    waivedFreight: String(draft.waivedFreight),
    freightInputDraftJson: draft.freightInputDraftJson,
    // 86d32t157 Phase 3 — what sales saw at save time. Server persists verbatim
    // (plus bracketId stamped from cache) and reads back at submit for drift check.
    pricingSnapshot: buildPricingSnapshot(draft.lineItems, baseMarkup),
    baseMarkupPct: draft.baseMarkupPct ?? 0,
    gradeMarkupsJson: draft.gradeMarkupsJson ?? null,
    extractionSource: draft.extractionSource,
    extractionText: draft.extractionText,
    extractionFileName: draft.extractionFileName,
    lineItems: draft.lineItems.map(toLineItemWire),
  };
}

function parseOriginalSpec(json: string | null): QuotationLineItemDto['originalSpec'] {
  if (!json) return null;
  try {
    return JSON.parse(json) as QuotationLineItemDto['originalSpec'];
  } catch {
    return null;
  }
}

function mapWireLineItems(lineItems: QuotationLineItemWireDto[]): QuotationLineItemDto[] {
  return lineItems
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((li) => ({
      id: li.clientId,
      diameterMm: li.diameterMm,
      lengthM: li.lengthM,
      gradeCode: li.gradeCode,
      isWeldable: li.isWeldable,
      quantity: li.quantity,
      unit: (li.unit === 'kgs' ? 'kgs' : 'pcs') as 'pcs' | 'kgs',
      weightKg: li.weightKg,
      matchStatus: li.matchStatus as QuotationLineItemDto['matchStatus'],
      rawDescription: li.rawDescription,
      notes: li.notes,
      matchedSalesDescription: li.matchedSalesDescription,
      matchedItemId: li.matchedItemId,
      priceLevel: (li.priceLevel ?? DEFAULT_PRICE_LEVEL) as QuotationLineItemDto['priceLevel'],
      basePricePerPc: li.basePricePerPc,
      averagePricePerPc: li.averagePricePerPc,
      originalSpec: parseOriginalSpec(li.originalSpecJson),
      isEdited: li.isEdited,
      possibleMatches: [],
      // CSC-291: persisted per-line promotion marker. Drives the badge + the AutoDraft
      // banner via the draft store's loadDraft hydration path.
      promotedFromGrade: li.promotedFromGrade ?? null,
    }));
}

function wireToDraft(wire: QuotationWireDto): QuotationDraft {
  // 14ykpqtr22u — cutting lists take pieces only: kilogram rows never enter the client. A draft
  // sheds them on its next save (rows missing from a save are deleted server-side); a posted
  // quotation keeps them in the database, unshown — they never posted. 14ykpqtw3hw: except a kg order the
  // cutting-list rule could not convert (QUO-R-003e), which is kept, unmatched. Nothing is normalized here.
  const { rows: lineItems, droppedKgLineCount } = dropQuotationKilogramRows(
    mapWireLineItems(wire.lineItems),
  );
  return {
    id: `srv-${wire.id}`,
    serverId: wire.id,
    quotationNumber: wire.quotationNumber,
    status: wire.status,
    baseMarkupPct: wire.baseMarkupPct,
    gradeMarkupsJson: wire.gradeMarkupsJson,
    cuttingListNumber: wire.cuttingListNumber,
    netSuiteUrl: wire.netSuiteUrl,
    postedAt: wire.postedAt,
    postedByName: wire.postedByName,
    createdByName: wire.createdByName,
    customerName: wire.customerName,
    headMainOffice: wire.headMainOffice,
    customerId: wire.customerId,
    customerEntityId: wire.customerEntityId,
    headMainOfficeCustomerId: wire.headMainOfficeCustomerId,
    projectCategoryId: wire.projectCategoryId,
    projectCategoryName: wire.projectCategoryName,
    projectName: wire.projectName,
    termsId: wire.termsId,
    termsName: wire.termsName,
    salesRepId: wire.salesRepId,
    salesCoordId: wire.salesCoordId,
    taxRate: wire.taxRate,
    date: wire.date,
    escalationDate: wire.escalationDate,
    expirationDate: wire.expirationDate,
    // Pickup/WaivedFreight are string-backed enums on the frontend. The server stores them as strings too.
    pickup: wire.pickup as QuotationDraft['pickup'],
    waivedFreight: wire.waivedFreight as QuotationDraft['waivedFreight'],
    // 86d32r9rv — pass raw JSON through; FreightCalculatorPanel + FreightChargeSummary parse on demand.
    freightInputDraftJson: wire.freightInputDraftJson ?? null,
    freightChargeJson: wire.freightChargeJson ?? null,
    freightSnapshotJson: wire.freightSnapshotJson ?? null,
    lineItems,
    droppedKgLineCount,
    submissionSnapshot: wire.submissionSnapshot ?? null,
    extractionStatus: 'idle',
    extractionError: null,
    extractionSource: (wire.extractionSource === 'intake' ? 'intake' : 'manual'),
    extractionSourceKind: null,
    extractionText: wire.extractionText,
    extractionFileName: wire.extractionFileName,
    extractionNotice: null,
    createdAt: wire.createdAt,
    updatedAt: wire.updatedAt ?? wire.createdAt,
    dirty: false,
    sourceEmail: wire.sourceEmail ?? null,
    attachments: wire.attachments ?? [],
    emailExtractionStatus: wire.extractionStatus ?? EmailExtractionStatus.NotApplicable,
    isArchived: wire.isArchived ?? false,
    archivedAt: wire.archivedAt ?? null,
    archivedById: wire.archivedById ?? null,
  };
}

// -----------------------------------------------------------------------------
// Service
// -----------------------------------------------------------------------------

class QuotationService {
  async listPaged(params: QuotationListParams = {}): Promise<QuotationListResult> {
    const { status, search, page = 1, pageSize = 10, sortBy, sortDesc = true, includeArchived } = params;
    const query: Record<string, string | number | boolean> = { page, pageSize, sortDesc };
    if (sortBy) query.sortBy = sortBy;
    if (status !== undefined) query.status = status;
    if (search) query.search = search;
    if (includeArchived) query.includeArchived = true;

    const { data } = await apiClient.get<ApiResponse<PagedResult<QuotationListItemDto>>>(
      '/quotations/paged',
      { params: query },
    );
    if (data.success && data.data) {
      const paged = data.data;
      return {
        items: paged.items,
        totalCount: paged.totalCount,
        totalPages: paged.totalPages,
        page: paged.page,
        pageSize: paged.pageSize,
      };
    }
    throw new Error(data.message || 'Failed to load quotations');
  }

  /**
   * Lightweight cross-status search for the autocomplete dropdown. Mirrors the
   * picklist closed-tab pattern: deliberately omits `status` so suggestions surface
   * matches from both Drafts and Submitted, and caps the page size at 8 to match
   * AsyncSearchSuggestions' default. Empty input short-circuits with an empty list
   * so we don't fire a wasted request when the user clears the box.
   */
  async search(query: string): Promise<QuotationListItemDto[]> {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const result = await this.listPaged({
      search: trimmed,
      page: 1,
      pageSize: 8,
      sortDesc: true,
    });
    return result.items;
  }

  async getStats(): Promise<QuotationStats> {
    const { data } = await apiClient.get<ApiResponse<QuotationStats>>('/quotations/stats');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load quotation stats');
  }

  async getById(id: number): Promise<QuotationDraft> {
    const { data } = await apiClient.get<ApiResponse<QuotationWireDto>>(`/quotations/${id}`);
    if (data.success && data.data) return wireToDraft(data.data);
    throw new Error(data.message || 'Quotation not found');
  }

  /**
   * CSC-297: workspace-list-shape projection of a single row. The realtime SSE
   * fetch-by-id path calls this on every relevant event so the master list can
   * splice in the freshly-changed row without paying for the full QuotationDraft
   * payload (line items, attachments, etc.). Returns null when the row is
   * missing or soft-deleted (404 is mapped to null so the caller can simply
   * splice the id out of the list without a separate code path).
   */
  async getRow(id: number): Promise<QuotationListItemDto | null> {
    try {
      const { data } = await apiClient.get<ApiResponse<QuotationListItemDto>>(`/quotations/${id}/row`);
      if (data.success && data.data) return data.data;
      return null;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  }

  async saveDraft(draft: QuotationDraft, baseMarkup: BaseMarkup | null = null): Promise<QuotationDraft> {
    const request = draftToSaveRequest(draft, baseMarkup);
    const { data } = await apiClient.post<ApiResponse<QuotationWireDto>>(
      '/quotations/draft',
      request,
    );
    if (data.success && data.data) return wireToDraft(data.data);
    throw new Error(data.message || 'Failed to save draft');
  }

  async deleteDraft(id: number): Promise<void> {
    const { data } = await apiClient.delete<ApiResponse<object>>(`/quotations/${id}`);
    if (!data.success) throw new Error(data.message || 'Failed to delete draft');
  }

  async archive(id: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<object>>(`/quotations/${id}/archive`);
    if (!data.success) throw new Error(data.message || 'Failed to archive quotation');
  }

  async restore(id: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<object>>(`/quotations/${id}/restore`);
    if (!data.success) throw new Error(data.message || 'Failed to restore quotation');
  }

  // CSC-289: idempotent per-user read receipt. Fire-and-forget by convention —
  // callers don't need to await unless they want to surface failures. Backend
  // INSERT … ON CONFLICT DO NOTHING means repeated calls are safe.
  async markAsRead(id: number): Promise<void> {
    await apiClient.post<ApiResponse<object>>(`/quotations/${id}/mark-read`);
  }

  /**
   * CSC-283 Layer 3: creates a brand-new manual Draft Quotation from N uploaded files.
   * Files marked `includeInExtraction` feed a single combined Gemini call; all files are
   * persisted under {root}/quotation/{quotationId}/. Returns the freshly-saved draft
   * (Status=Draft) so the caller can navigate straight to compose.
   */
  async createDraftFromFiles(
    files: { file: File; includeInExtraction: boolean }[],
    bodyText?: string,
  ): Promise<QuotationDraft> {
    if (files.length === 0) {
      throw new Error('At least one file is required');
    }
    if (!files.some((f) => f.includeInExtraction)) {
      throw new Error('At least one file must be marked for extraction');
    }

    const form = new FormData();
    for (const entry of files) {
      // The multipart spec lets us repeat the same field name for arrays. Backend reads
      // [FromForm] List<IFormFile> files / List<string> includeFlags in lockstep order.
      form.append('files', entry.file, entry.file.name);
      form.append('includeFlags', entry.includeInExtraction ? 'true' : 'false');
    }
    // Pasted text rides along as the email body so it's extracted (and deduped) together
    // with the files rather than dropped (86d3hdy52).
    const trimmedBody = bodyText?.trim();
    if (trimmedBody) form.append('bodyText', trimmedBody);

    const { data } = await apiClient.post<ApiResponse<QuotationWireDto>>(
      '/quotations/draft/from-files',
      form,
      { timeout: 180_000, headers: { 'Content-Type': 'multipart/form-data' } },
    );
    if (data.success && data.data) return wireToDraft(data.data);
    throw new Error(data.message || 'Failed to create draft from files');
  }

  /**
   * Re-runs stage-2 extraction for an AutoDraft against a chosen subset of source-email
   * attachments. Body is always included alongside the selected set in a single combined
   * Gemini call; the result REPLACES line items on the draft.
   *
   * - `attachmentIds` null / empty → backend defaults to "all real (non-signature)
   *   attachments." This is the right default for trivial cases (1 PDF, or N photos of
   *   the same RFQ split across pages) so sales doesn't have to multi-select the
   *   obvious set.
   * - Explicit ids → backend uses exactly that subset, bypassing the signature filter
   *   (sales is overriding the heuristic explicitly).
   */
  async retryExtraction(id: number, attachmentIds?: number[]): Promise<QuotationDraft> {
    const body = attachmentIds && attachmentIds.length > 0
      ? { attachmentIds }
      : { attachmentIds: null };
    const { data } = await apiClient.post<ApiResponse<QuotationWireDto>>(
      `/quotations/${id}/retry-extraction`,
      body,
      { timeout: 90_000 },
    );
    if (data.success && data.data) return wireToDraft(data.data);
    throw new Error(data.message || 'Retry extraction failed');
  }

  // 86d3jkwuq async posting — a fast CLAIM, not the post. Returns once the server has enqueued the
  // posting job (HTTP 202, no DTO). The background worker runs the NetSuite Estimate post and the
  // result (Submitted / Failed) streams back over the quotations SSE stream.
  // 86d3mnuxe FIX 5 — the claim now runs the pricing decision synchronously: drift → HTTP 409 with
  // the before/after detail, rethrown here as PricingDriftError so the panel opens the drift dialog
  // ([Keep quoted pricing] resubmits with acceptQuotedPricing; [Use current] re-saves + resubmits).
  async submitCuttingList(
    draft: QuotationDraft,
    baseMarkup: BaseMarkup | null,
    opts?: { acceptQuotedPricing?: boolean },
  ): Promise<void> {
    if (!draft.serverId) {
      throw new Error('Save the draft before submitting a cutting list.');
    }

    const ctx = computePricingContext(draft.lineItems);
    const adjustment = ctx?.adjustment ?? 0;

    const markupPct = (code: string): number => {
      const base = markupForGrade(code, baseMarkup);
      if (base == null) return 0;
      return Number(((base + adjustment) * 100).toFixed(2));
    };

    // CSC-307: the workspace's pre-submit guard (QuotationDetailPanel.handleSubmit)
    // already blocks submits where any editable line is unmatched / zero-qty / zero-weight,
    // so we no longer silently filter those out here. The remaining `unit === 'pcs'`
    // filter is intentional and unrelated to that bug — the cutting-list payload has no
    // unit field, so kg-unit lines can't be safely posted to NetSuite as-is. They stay
    // dropped to avoid mis-posting `quantity: 1500 (kg)` as 1500 pcs.
    // 14ykpqtr22u — the same rule (isPostedRow) now decides the tier, the freight weight and the
    // totals, so what the screen counts and what posts can't disagree.
    const items = draft.lineItems
      .filter(isPostedRow)
      .map((li) => {
        // `rate` → base per-pc rate (Base or Average price, depending on priceLevel).
        // `rateWithMarkup` → rate × (1 + base-per-grade markup + tier/size-mix adjustment).
        // computePricing returns null for Custom priceLevel or when markup/price data is
        // missing — in those cases we omit the rate fields and NetSuite defaults apply.
        const breakdown = computePricing(li, baseMarkup, adjustment);
        return {
          matchedItemId: li.matchedItemId!,
          quantity: li.quantity!,
          itemCode: li.matchedSalesDescription ?? undefined,
          rate: breakdown?.rate ?? null,
          rateWithMarkup: breakdown?.rateWithMarkup ?? null,
        };
      });

    if (items.length === 0) {
      // Only reachable now when every editable line is kg-unit (the client guard
      // accepts matched kg-unit lines but the unit='pcs' filter above strips them).
      throw new Error(
        'No eligible line items to post. Cutting lists currently only accept lines priced per piece (pcs).',
      );
    }

    const request = {
      quotationId: draft.serverId,
      customerId: draft.customerId ?? '',
      headMainOfficeCustomerId: draft.headMainOfficeCustomerId ?? undefined,
      salesRepId: draft.salesRepId ?? '',
      salesCoordinatorId: draft.salesCoordId ?? '',
      termsId: draft.termsId ?? undefined,
      date: draft.date,
      escalationDate: draft.escalationDate,
      expirationDate: draft.expirationDate,
      projectName: draft.projectName.toUpperCase(),
      pickup: Number(draft.pickup),
      waivedFreight: Number(draft.waivedFreight),
      gradeMarkups: {
        grade33: markupPct('33'),
        grade40: markupPct('40'),
        grade60: markupPct('60'),
        grade75: markupPct('75'),
        grade80: markupPct('80'),
      },
      items,
      // 86d3mnuxe FIX 5 — the drift dialog's [Keep quoted pricing] answer.
      acceptQuotedPricing: opts?.acceptQuotedPricing ?? false,
      // 14ykpqtr22u — the size rule this page prices with; the server refuses a mismatch
      // (a tab left open across a deploy) before anything runs.
      pricingRuleVersion: PRICING_RULE_VERSION,
    };

    // 86d3mnuxe FIX 5 — the claim performs the decide-at-submit pass (live base-markup pull + drift
    // check), so drift is a synchronous 409 again (it briefly rode the async failure card between
    // 86d3jkwuq and FIX 5). Rethrow it typed so panels open the drift dialog with the detail.
    try {
      const { data } = await apiClient.post<ApiResponse<null>>('/cutting-lists', request);
      if (!data.success) {
        throw new Error(data.message || 'Failed to start cutting list posting');
      }
    } catch (err) {
      const drift = extractPricingDrift(err);
      if (drift) throw new PricingDriftError(drift);
      throw err;
    }
  }

  // 86d3mnuxe funnel — close a NotReached-failed posting (Failed → Cancelled) so the draft accepts a
  // fresh submit. The BE refuses (400) when the post may have reached NetSuite (reconcile instead).
  async cancelCuttingListPosting(quotationId: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<null>>(
      `/cutting-lists/${quotationId}/cancel-posting`,
    );
    if (!data.success) throw new Error(data.message || 'Failed to cancel the posting');
  }

  // 86d3mnuxe funnel — Reconcile a REACHED failure: the BE re-arms the same job (frozen decision) so
  // the adopt recovers the Estimate NetSuite already accepted. Never re-decides, never prompts.
  async reconcileCuttingListPosting(quotationId: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<null>>(
      `/cutting-lists/${quotationId}/reconcile-posting`,
    );
    if (!data.success) throw new Error(data.message || 'Failed to start the reconcile');
  }
}

export const quotationService = new QuotationService();
